/**
 * The Data Source screen's plain functions: the file pre-checks and upload, the
 * REST call body, the refusal sentences and the dropped-secret labels.
 *
 * Plain functions on purpose: whatever takes a secret (a password, a token, a
 * header, a body) is never a MobX action, so no spy() listener sees it.
 */
import { failureMessage } from "../../tickets/TicketAttachmentsStore";
import {
  REST_HOST_NOT_ALLOWED_MESSAGE,
  REST_HOST_PRIVATE_MESSAGE,
  SQL_HOST_NOT_ALLOWED_MESSAGE,
  refusalReason,
  type DataMapperFileType,
  type DataMapperRestSecrets,
  type DataMapperSourceType,
} from "../dataMapper.types";
import { loadErrorMessage } from "shared/loadOrError";

// ---- Files ---------------------------------------------------------------------

/** The names each file type accepts (the API takes .csv, .json, .xlsx and .xls: source-files.ts). */
export const FILE_EXTENSIONS: Record<DataMapperFileType, readonly string[]> = {
  csv: [".csv"],
  excel: [".xlsx", ".xls"],
  json: [".json"],
};

/**
 * The upload cap the API applies by default (env DATA_MAPPER_UPLOAD_MAX_MB, default 100).
 * shortcut: the web cannot read the API's env, so a deployment that LOWERS the cap is caught
 * by the API's own refusal (its message is shown) and one that RAISES it is still held to 100
 * here. Expose the cap on a settings read if a deployment ever raises it.
 */
export const DATA_MAPPER_UPLOAD_MAX_MB = 100;
export const DATA_MAPPER_UPLOAD_MAX_BYTES = DATA_MAPPER_UPLOAD_MAX_MB * 1024 * 1024;

export const SOURCE_FILE_CATEGORY = "data-mapper-source-file";
export const SOURCE_FILE_ENTITY_TYPE = "data-mapper";

export const formatFileSize = (bytes: number): string =>
  bytes < 1024
    ? `${bytes} B`
    : bytes < 1024 * 1024
      ? `${(bytes / 1024).toFixed(1)} KB`
      : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/** Why this file cannot be uploaded as this type, or null when it can. Checked before any byte is sent. */
export function checkSourceFile(file: { name: string; size: number }, type: DataMapperFileType): string | null {
  const lower = file.name.toLowerCase();
  const allowed = FILE_EXTENSIONS[type];
  if (!allowed.some((ext) => lower.endsWith(ext))) {
    return `${file.name} is not a ${allowed.join(" or ")} file. Choose a ${allowed.join(" or ")} file, or change the source type.`;
  }
  if (file.size === 0) return `${file.name} is empty.`;
  if (file.size > DATA_MAPPER_UPLOAD_MAX_BYTES) {
    return `${file.name} is ${formatFileSize(file.size)}, more than the ${DATA_MAPPER_UPLOAD_MAX_MB} MB HQ accepts for a source file.`;
  }
  return null;
}

/** What the documents upload answers (the fields this screen reads). */
export interface UploadedSourceFile {
  id: string;
  originalFileName: string;
  fileSize: number;
}

export interface UploadOptions {
  apiUrl: string;
  token: string | null;
  onProgress: (percent: number) => void;
}

/** A started upload: its answer, and a way to stop it. */
export interface UploadHandle {
  done: Promise<UploadedSourceFile>;
  abort: () => void;
}

/**
 * Upload a source file to HQ's documents store (data-mapper/source-files.ts):
 *
 *   POST /documents/upload?entityType=data-mapper&category=data-mapper-source-file
 *   Authorization: Bearer <jwt>
 *   Content-Type: application/octet-stream   X-File-Name: <encoded name>   body: the File
 *
 * application/octet-stream for EVERY type: HQ's body parser would consume a
 * text/* or JSON body before the upload route sees it. XMLHttpRequest rather
 * than fetch, because only it reports upload progress. The query names no
 * entityId: the API makes the uploader the owner whatever it says.
 */
export function uploadSourceFile(file: File, opts: UploadOptions): UploadHandle {
  const xhr = new XMLHttpRequest();
  const query = new URLSearchParams({ entityType: SOURCE_FILE_ENTITY_TYPE, category: SOURCE_FILE_CATEGORY });
  const done = new Promise<UploadedSourceFile>((resolve, reject) => {
    xhr.open("POST", `${opts.apiUrl}/documents/upload?${query}`);
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.setRequestHeader("X-File-Name", encodeURIComponent(file.name));
    if (opts.token) xhr.setRequestHeader("Authorization", `Bearer ${opts.token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) opts.onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
    };
    xhr.onerror = () => reject(new Error("The upload could not reach HQ. Check your connection and try again."));
    xhr.onabort = () => reject(new Error("The upload was cancelled."));
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        // The tickets screen's reading of an upload refusal, shared rather than copied.
        void failureMessage(new Response(xhr.responseText, { status: xhr.status }), "Upload").then(
          (message) => reject(new Error(message)),
          () => reject(new Error(`Upload failed (${xhr.status})`))
        );
        return;
      }
      try {
        const doc = JSON.parse(xhr.responseText) as Partial<UploadedSourceFile>;
        if (typeof doc.id !== "string" || !doc.id) throw new Error("no id");
        resolve({
          id: doc.id,
          originalFileName: typeof doc.originalFileName === "string" ? doc.originalFileName : file.name,
          fileSize: typeof doc.fileSize === "number" ? doc.fileSize : file.size,
        });
      } catch {
        reject(new Error("HQ accepted the upload but did not say which file it stored. Upload it again."));
      }
    };
    xhr.send(file);
  });
  return { done, abort: () => xhr.abort() };
}

// ---- REST ----------------------------------------------------------------------

/**
 * Names that suggest a credential: config-secrets.ts `isSensitiveFieldName`, mirrored.
 * shortcut: a twin of the API's rule kept in step by hand; the API's strip on save stays the
 * authority, this only keeps a key typed into the URL out of the store. Share it if it drifts.
 */
const SENSITIVE_NAME_RE =
  /^(authorization|proxy-authorization|cookie|set-cookie)$|api[-_]?key|token|secret|passw|credential|session|signature|auth/i;
const SENSITIVE_EXACT = new Set(["key", "sig", "pwd", "pass", "code"]);
export const isSensitiveFieldName = (name: string): boolean =>
  SENSITIVE_EXACT.has(name.toLowerCase()) || SENSITIVE_NAME_RE.test(name);

/**
 * A typed REST URL without the parts that can carry a credential (config-secrets.ts): a user
 * name or password, credential-named query parameters, and the #fragment. `removed` names what
 * went, for the form to say so. Text that is not (yet) a URL passes through unchanged.
 */
export function sanitizeRestUrl(raw: string): { url: string; removed: string[] } {
  let parsed: URL;
  try {
    parsed = new URL(raw.trim());
  } catch {
    return { url: raw, removed: [] };
  }
  const removed: string[] = [];
  if (parsed.username || parsed.password) {
    parsed.username = "";
    parsed.password = "";
    removed.push("the user name and password");
  }
  for (const name of [...new Set(parsed.searchParams.keys())]) {
    if (isSensitiveFieldName(name)) {
      parsed.searchParams.delete(name);
      removed.push(`the "${name}" parameter`);
    }
  }
  if (parsed.hash) {
    parsed.hash = "";
    removed.push("the #fragment");
  }
  return removed.length ? { url: parsed.toString(), removed } : { url: raw, removed };
}

/** The scheme + host + port a secret was typed for (secrets do not follow the URL to another). */
export const restOrigin = (url: string): string => {
  try {
    return new URL(url).origin;
  } catch {
    return "";
  }
};

/**
 * A REST call's config: the store's non-secret settings plus — only here, for
 * this one body — the secrets the form holds. Blank header names are skipped.
 */
export function restConfigWithSecrets(
  base: Record<string, unknown>,
  secrets: DataMapperRestSecrets
): Record<string, unknown> {
  const headers: Record<string, string> = {};
  for (const h of secrets.headers) {
    const name = h.name.trim();
    if (name) headers[name] = h.value;
  }
  const baseAuth = (base.auth ?? { type: "none" }) as Record<string, unknown>;
  const auth: Record<string, unknown> = { ...baseAuth };
  if (baseAuth.type === "bearer" && secrets.token) auth.token = secrets.token;
  if (baseAuth.type === "basic" && secrets.password) auth.password = secrets.password;
  if (baseAuth.type === "api-key" && secrets.apiKeyValue) auth.headerValue = secrets.apiKeyValue;
  const config: Record<string, unknown> = { ...base, headers, auth };
  if (base.method === "POST" && secrets.body.trim() !== "") config.body = secrets.body;
  return config;
}

// ---- Refusals as sentences --------------------------------------------------------

const detail = (err: unknown): string => {
  const message = (err as { message?: unknown } | null)?.message;
  return typeof message === "string" && message.trim() ? ` ${message.trim()}` : "";
};

/**
 * A data-plane refusal as the sentence the screen shows: what happened and what
 * to do, with the API's own (secret-free) message as the detail where it adds one.
 */
export function dataSourceErrorMessage(err: unknown, fallback: string, type?: DataMapperSourceType): string {
  const reason = refusalReason(err);
  switch (reason) {
    case "sql-host-not-allowed":
      return SQL_HOST_NOT_ALLOWED_MESSAGE;
    case "rest-host-not-allowed": {
      // The API refuses a LISTED host that resolves to a private address too; its message says
      // which (rest-host-policy.ts), and the allowlist sentence would send the admin the wrong way.
      const message = String((err as { message?: unknown }).message ?? "");
      return /private|loopback|link-local|metadata/i.test(message)
        ? REST_HOST_PRIVATE_MESSAGE
        : REST_HOST_NOT_ALLOWED_MESSAGE;
    }
    case "file-too-large":
      return `The file is larger than HQ reads.${detail(err)} Split it, or remove the rows, columns or sheets you do not need, and upload it again.`;
    case "source-too-large":
      return type === "rest-api"
        ? `The API returned more data than HQ holds for browsing.${detail(err)} Ask for fewer pages (Max pages), a smaller page size or a narrower response path.`
        : `The source is larger than HQ holds for browsing.${detail(err)} Use a smaller file, or fewer sheets.`;
    case "file-unreadable":
      return `HQ could not read the file.${detail(err)} Check that the source type and options (delimiter, encoding, root path, sheets) match the file.`;
    case "file-not-found":
      return "That uploaded file is not available to you any more. Upload it again.";
    case "rest-request-failed":
      return `The API call failed.${detail(err)} Check the URL, method and authentication, then test again.`;
    case "too-many-open-sources":
      return `Too many file or API sources are open.${detail(err)}`;
    case "not-yet-ported":
    case "unsupported-source-type":
      return "HQ's API cannot read this kind of source yet.";
    default:
      return loadErrorMessage(err, fallback);
  }
}

// ---- What a save dropped -------------------------------------------------------------

/** One `droppedSecrets` path (config-secrets.ts) as words. */
export function droppedSecretLabel(path: string): string {
  if (path === "password") return "the password";
  if (path === "auth.token") return "the bearer token";
  if (path === "auth.password") return "the basic-auth password";
  if (path === "auth.headerValue") return "the API key value";
  if (path === "body") return "the request body (it is not JSON or a form, so HQ cannot tell whether it holds a credential)";
  if (path === "url") return "the URL (HQ could not check it for a credential)";
  if (path === "url.credentials") return "the user name and password in the URL";
  if (path === "url.fragment") return "the URL's #fragment";
  if (path === "filePath") return "a server file path";
  if (path === "bakRestore") return "the backup-restore paths";
  if (path.startsWith("headers.")) return `the ${path.slice("headers.".length)} header`;
  if (path.startsWith("url.query.")) return `the URL parameter "${path.slice("url.query.".length)}"`;
  if (path.startsWith("body.")) return `the body field "${path.slice("body.".length)}"`;
  return path;
}
